import React, { useContext, useEffect, useRef, useState } from 'react'
import cn from 'classnames'
import { Typography } from '@grammarly/badger-ui'
import { TypeComponentIframe } from '../../lib/types/component-iframe'
import { ContentProps } from '../renderer'
import styles from './iframe.module.scss'
import heroCardStyles from '../ui-lib/hero/card.module.scss'
import { useBackgroundStyle } from '../page-section/theme'
import { SectionThemeContext } from '../../contexts/section-theme-context'
import { ImageAsset } from '../asset/image'
import { getConfig } from '../../config'
import { composeUrl } from '../../lib/utils/url'
import { useGnarContainerId } from '../../lib/hooks'
import { gnarSpec } from '../../lib/tracking/gnar'
import { feMetrics } from '@lib/tracking/metrics'
import * as CmsMetrics from '@lib/tracking/metrics/metrics'
import { PageContext } from '../../contexts/page-context'

interface HeightChangeData {
  payload: { height: number }
}

interface Size {
  height: number
}

const isHeightChangeData = (data: unknown): data is HeightChangeData => {
  return (
    typeof data === 'object' &&
    data != null &&
    'payload' in data &&
    typeof data.payload === 'object' &&
    data.payload != null &&
    'height' in data.payload &&
    typeof data.payload.height === 'number'
  )
}

const errorDelay = 3000

export const IframeComponent: React.FC<ContentProps<TypeComponentIframe>> = ({ structure }) => {
  const iframeRef = useRef<HTMLIFrameElement | null>(null)
  const { pageId, slug } = useContext(PageContext)
  const { background, url, height, title, headerImage, headerBackground, heading, internalName } = structure.fields
  const [size, setSize] = useState<Size>({ height })
  const { containerId } = useGnarContainerId()
  const startTimeRef = useRef(Date.now())
  const slowLoadTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const handleIframeMessage = (ev: MessageEvent) => {
        const { origin, data } = ev
        const urlObject = new URL(url)
        const domain = `${urlObject.protocol}//${urlObject.hostname}`
        if (!origin.match(domain) || !isHeightChangeData(data)) {
          return
        }
        if (data.payload.height !== size.height) {
          console.info(
            'Form initial size did not match render. Rendered size: ' + JSON.stringify({ height: data.payload.height })
          )
        }
        const timeInSeconds = (Date.now() - startTimeRef.current) / 1000
        gnarSpec.iframeMessage(pageId, slug, url, timeInSeconds)
        if (slowLoadTimeoutRef.current) {
          clearTimeout(slowLoadTimeoutRef.current)
        }
        feMetrics
          .getMetric(CmsMetrics.GeneralClientMetrics.Iframe)
          .getCounter(CmsMetrics.ClientMetricsActions.Success)
          .setContext({
            pageSlug: window.location.pathname === '/' ? window.location.pathname : window.location.pathname.slice(1),
          })
          .increment()
        setSize(data.payload)
      }

      window.addEventListener('message', handleIframeMessage)
      return () => window.removeEventListener('message', handleIframeMessage)
    }
  }, [size.height, url, pageId, slug])

  useEffect(() => {
    iframeRef.current?.contentWindow?.postMessage('requestSize', '*')

    feMetrics
      .getMetric(CmsMetrics.GeneralClientMetrics.Iframe)
      .getCounter(CmsMetrics.ClientMetricsActions.Attempt)
      .setContext({
        pageSlug: window.location.pathname === '/' ? window.location.pathname : window.location.pathname.slice(1),
      })
      .increment()
  }, [])

  useEffect(() => {
    slowLoadTimeoutRef.current = setTimeout(() => {
      const time = (Date.now() - startTimeRef.current) / 1000
      gnarSpec.iframeSlowLoad(pageId, slug, url, time)
      feMetrics
        .getMetric(CmsMetrics.GeneralClientMetrics.Iframe)
        .getCounter(CmsMetrics.ClientMetricsActions.Failure)
        .setContext({
          pageSlug: window.location.pathname === '/' ? window.location.pathname : window.location.pathname.slice(1),
        })
        .increment()
    }, errorDelay)
  }, [pageId, url, slug])

  const theme = useContext(SectionThemeContext)
  const backgroundStyle = useBackgroundStyle(theme, background?.color)
  const headerBackgroundStyle = useBackgroundStyle(theme, headerBackground?.color)
  // Note: broken on localhost because our url.main on localhost is qagr
  const origin = IS_CLIENT_SIDE ? document.location.origin : getConfig().urls.main

  return (
    <div className={cn(heroCardStyles.heroCard, ...backgroundStyle)} data-component-id={internalName}>
      {headerImage && (
        <div className={cn(heroCardStyles.imageContainer, ...headerBackgroundStyle)}>
          <ImageAsset className={heroCardStyles.image} structure={headerImage} />
        </div>
      )}
      <div className={heroCardStyles.content}>
        {heading && (
          <Typography.T3 as="h2" className={heroCardStyles.title}>
            {heading}
          </Typography.T3>
        )}
        <iframe
          ref={iframeRef}
          title={title}
          className={cn(styles.iframe)}
          // This is likely Pardot-specific and it may be more appropriate to move into the content model as an option
          src={composeUrl(url, { origin, GNAR_Container_ID: containerId })}
          height={size.height}
        />
      </div>
    </div>
  )
}
